(() => {
  "use strict";

  const page = document.querySelector("#EVTOLProduct");
  if (!page) return;

  const emit = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent("dsp:cro", { detail: { name, ...detail } }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: `dsp_${name}`, ...detail });
  };

  const initStickyOffset = () => {
    const header = document.querySelector(".section-header") || document.querySelector('[id^="shopify-section-"][id$="__header"]');
    const productNav = page.querySelector(".evtol-product-nav-section");

    let pendingFrame = 0;
    let lastOffset = -1;
    let lastContentOffset = -1;
    let lastFilmHeaderHeight = -1;
    const update = () => {
      pendingFrame = 0;
      let offset = 0;
      let filmHeaderHeight = 0;

      if (header) {
        const style = getComputedStyle(header);
        const rect = header.getBoundingClientRect();
        const sticky = style.position === "sticky" || style.position === "fixed";
        filmHeaderHeight = Math.max(0, Math.ceil(rect.height));
        offset = sticky && rect.top <= (parseFloat(style.top) || 0) + 1 && rect.bottom > 0
          ? Math.max(0, Math.ceil(rect.bottom) - 1)
          : 0;
      }

      const productNavHeight = productNav && getComputedStyle(productNav).display !== "none"
        ? Math.ceil(productNav.getBoundingClientRect().height)
        : 0;
      const contentOffset = offset + productNavHeight;

      if (offset !== lastOffset) {
        lastOffset = offset;
        page.style.setProperty("--evtol-sticky-offset", `${offset}px`);
      }
      if (contentOffset !== lastContentOffset) {
        lastContentOffset = contentOffset;
        page.style.setProperty("--evtol-content-sticky-offset", `${contentOffset}px`);
      }
      if (filmHeaderHeight !== lastFilmHeaderHeight) {
        lastFilmHeaderHeight = filmHeaderHeight;
        page.style.setProperty("--evtol-film-header-height", `${filmHeaderHeight}px`);
      }
    };
    const schedule = () => {
      if (pendingFrame) return;
      pendingFrame = requestAnimationFrame(update);
    };

    update();
    if ("ResizeObserver" in window) {
      const observer = new ResizeObserver(schedule);
      if (header) observer.observe(header);
      if (productNav) observer.observe(productNav);
    }
    addEventListener("resize", schedule, { passive: true });
    addEventListener("scroll", schedule, { passive: true });
  };

  const attachSwipe = (element, onSwipe) => {
    if (!element) return () => {};
    let startX = 0;
    let startY = 0;
    let tracking = false;
    const start = event => {
      if (event.touches.length !== 1) return;
      tracking = true;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    };
    const end = event => {
      if (!tracking || event.changedTouches.length !== 1) return;
      tracking = false;
      const dx = event.changedTouches[0].clientX - startX;
      const dy = event.changedTouches[0].clientY - startY;
      if (Math.abs(dx) < 48 || Math.abs(dx) <= Math.abs(dy) * 1.2) return;
      onSwipe(dx < 0 ? 1 : -1);
    };
    element.addEventListener("touchstart", start, { passive: true });
    element.addEventListener("touchend", end, { passive: true });
    return () => {
      element.removeEventListener("touchstart", start);
      element.removeEventListener("touchend", end);
    };
  };

  const initGallery = gallery => {
    if (gallery.dataset.evtolReady === "true") return;
    gallery.dataset.evtolReady = "true";
    const slides = [...gallery.querySelectorAll("[data-gallery-slide]")];
    const captions = [...gallery.querySelectorAll("[data-gallery-caption]")];
    const inlineThumbs = [...gallery.querySelectorAll(".evtol-gallery__thumbs [data-gallery-thumb]")];
    const count = gallery.querySelector("[data-gallery-count]");
    const dialog = gallery.querySelector("[data-gallery-dialog]");
    const lightboxImages = [...gallery.querySelectorAll("[data-gallery-lightbox-image]")];
    const lightboxCount = gallery.querySelector("[data-lightbox-count]");
    const lightboxTitle = gallery.querySelector("[data-lightbox-title]");
    const lightboxCaption = gallery.querySelector("[data-lightbox-caption]");
    const lightboxThumbRail = gallery.querySelector("[data-lightbox-thumbs]");
    const stage = gallery.querySelector("[data-gallery-stage]");
    const lightboxStage = gallery.querySelector("[data-lightbox-stage]");
    if (!slides.length) return;

    inlineThumbs.forEach(thumb => lightboxThumbRail?.append(thumb.cloneNode(true)));
    const thumbs = [...gallery.querySelectorAll("[data-gallery-thumb]")];

    let active = 0;
    let returnTarget = null;

    const render = (scrollThumb = false) => {
      active = (active + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const selected = index === active;
        slide.hidden = !selected;
        slide.classList.toggle("is-active", selected);
      });
      captions.forEach((caption, index) => { caption.hidden = index !== active; });
      thumbs.forEach((thumb, index) => thumb.setAttribute("aria-current", String(index === active)));
      lightboxImages.forEach((image, index) => {
        const selected = index === active;
        image.hidden = !selected;
        image.classList.toggle("is-active", selected);
      });
      const currentCaption = captions[active];
      const title = currentCaption?.querySelector("strong")?.textContent?.trim() || "Product image";
      const copy = currentCaption?.querySelector("span")?.textContent?.trim() || "";
      if (count) count.textContent = `${active + 1} / ${slides.length}`;
      if (lightboxCount) lightboxCount.textContent = `${active + 1} / ${slides.length}`;
      if (lightboxTitle) lightboxTitle.textContent = title;
      if (lightboxCaption) lightboxCaption.textContent = copy;
      if (scrollThumb && !dialog?.open) thumbs[active]?.scrollIntoView({ block: "nearest", inline: "nearest" });
    };

    const move = (direction, source) => {
      active += direction;
      render(true);
      emit("gallery_image_viewed", { index: active + 1, source });
    };

    const open = trigger => {
      if (!dialog?.showModal) return;
      returnTarget = trigger;
      render();
      dialog.showModal();
      document.documentElement.classList.add("evtol-gallery-is-open");
      dialog.querySelector("[data-gallery-close]")?.focus();
      emit("gallery_opened", { index: active + 1 });
    };

    const close = () => {
      if (dialog?.open) dialog.close();
    };

    const click = event => {
      const thumb = event.target.closest("[data-gallery-thumb]");
      if (thumb) {
        active = Number(thumb.dataset.galleryThumb);
        render(true);
        emit("gallery_image_viewed", { index: active + 1, source: "thumbnail" });
        return;
      }
      const slide = event.target.closest("[data-gallery-slide]");
      if (slide) {
        active = Number(slide.dataset.gallerySlide);
        open(slide);
        return;
      }
      if (event.target.closest("[data-gallery-prev]")) move(-1, dialog?.open ? "lightbox_arrow" : "arrow");
      if (event.target.closest("[data-gallery-next]")) move(1, dialog?.open ? "lightbox_arrow" : "arrow");
      if (event.target.closest("[data-gallery-close]")) close();
      if (event.target === dialog) close();
    };

    const keydown = event => {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        if (dialog?.open || gallery.contains(document.activeElement)) {
          event.preventDefault();
          move(event.key === "ArrowLeft" ? -1 : 1, "keyboard");
        }
      }
    };

    const onClose = () => {
      document.documentElement.classList.remove("evtol-gallery-is-open");
      returnTarget?.focus();
    };

    gallery.addEventListener("click", click);
    gallery.addEventListener("keydown", keydown);
    dialog?.addEventListener("close", onClose);
    const removeStageSwipe = attachSwipe(stage, direction => move(direction, "swipe"));
    const removeLightboxSwipe = attachSwipe(lightboxStage, direction => move(direction, "lightbox_swipe"));
    render();

    gallery._evtolCleanup = () => {
      gallery.removeEventListener("click", click);
      gallery.removeEventListener("keydown", keydown);
      dialog?.removeEventListener("close", onClose);
      removeStageSwipe();
      removeLightboxSwipe();
    };
  };

  const initAccordion = accordion => {
    if (accordion.dataset.evtolReady === "true") return;
    accordion.dataset.evtolReady = "true";
    const click = event => {
      const button = event.target.closest("button[aria-expanded]");
      if (!button) return;
      const willOpen = button.getAttribute("aria-expanded") !== "true";
      accordion.querySelectorAll("button[aria-expanded]").forEach(item => item.setAttribute("aria-expanded", "false"));
      button.setAttribute("aria-expanded", String(willOpen));
    };
    accordion.addEventListener("click", click);
    accordion._evtolCleanup = () => accordion.removeEventListener("click", click);
  };

  const initEstimator = estimator => {
    if (estimator.dataset.evtolReady === "true") return;
    estimator.dataset.evtolReady = "true";
    const down = estimator.querySelector("[data-down-payment]");
    const term = estimator.querySelector("[data-term]");
    const apr = estimator.querySelector("[data-apr]");
    const option = estimator.querySelector("[data-option]");
    if (!down || !term || !apr || !option) return;
    const currency = estimator.dataset.currency || "USD";
    const formatter = new Intl.NumberFormat(document.documentElement.lang || "en-US", { style: "currency", currency, maximumFractionDigits: 0 });
    const money = value => formatter.format(Math.round(value));
    const update = () => {
      const base = Number(estimator.dataset.basePrice) || 0;
      const optionPrice = option.checked ? Number(estimator.dataset.optionPrice) || 0 : 0;
      const total = base + optionPrice;
      const principal = Math.max(0, total - Number(down.value));
      const months = Number(term.value);
      const monthlyRate = Number(apr.value) / 100 / 12;
      const payment = monthlyRate ? principal * monthlyRate / (1 - Math.pow(1 + monthlyRate, -months)) : principal / months;
      estimator.querySelector("[data-estimate-total]").textContent = money(total);
      estimator.querySelector("[data-down-output]").textContent = money(Number(down.value));
      estimator.querySelector("[data-term-output]").textContent = `${months} months`;
      estimator.querySelector("[data-apr-output]").textContent = `${Number(apr.value).toFixed(2)}%`;
      estimator.querySelector("[data-monthly-payment]").textContent = `${money(payment)}/mo`;
    };
    estimator.addEventListener("input", update);
    estimator._evtolCleanup = () => estimator.removeEventListener("input", update);
    update();
  };

  const initScrollFilm = section => {
    if (section.dataset.evtolReady === "true") return;
    section.dataset.evtolReady = "true";
    const stage = section.querySelector("[data-film-stage]");
    const canvas = section.querySelector("[data-film-canvas]");
    const poster = section.querySelector("[data-film-poster]");
    const loader = section.querySelector("[data-film-loader]");
    const loaderProgress = section.querySelector("[data-film-loader-progress]");
    const finalBeat = section.querySelector("[data-film-final]");
    const finalLink = finalBeat?.querySelector("a");
    const configNode = section.querySelector("[data-film-config]");
    const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!stage || !canvas || !configNode || reducedMotion) return;

    let config;
    try { config = JSON.parse(configNode.textContent); } catch (error) { return; }
    const frames = Array.isArray(config.frames) ? config.frames : [];
    const completedLoaderLabel = typeof config.completedLoaderLabel === "string" ? config.completedLoaderLabel : "Prepare for takeoff";
    const frameCount = frames.length;
    if (!frameCount) return;
    const frameWidth = Number(config.frameWidth) || 1264;
    const frameHeight = Number(config.frameHeight) || 720;
    const freezeFrame = Math.max(0, Math.min(frameCount - 1, Number(config.freezeFrame ?? 80)));
    const uniformBackdrop = typeof config.uniformBackdrop === "string" ? config.uniformBackdrop : "";
    const mobileLiftStart = Number(config.mobileLiftStart) || .16;
    const openingStartsHidden = Boolean(config.openingStartsHidden);
    const openingStart = Number(config.openingStart) || 0;
    const openingInDuration = Number(config.openingInDuration) || .08;
    const openingHoldEnd = Number(config.openingHoldEnd) || .28;
    const openingOutDuration = Number(config.openingOutDuration) || .14;
    const openingStartDesktop = Number(config.openingStartDesktop) || openingStart;
    const openingInDurationDesktop = Number(config.openingInDurationDesktop) || openingInDuration;
    const openingHoldEndDesktop = Number(config.openingHoldEndDesktop) || openingHoldEnd;
    const openingOutDurationDesktop = Number(config.openingOutDurationDesktop) || openingOutDuration;
    const finalStartDesktop = Number(config.finalStartDesktop) || .62;
    const finalStartMobile = Number(config.finalStartMobile) || .60;
    const finalDuration = Number(config.finalDuration) || .20;
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) return;

    const abortController = new AbortController();
    const frameBlobs = Array(frameCount);
    const bitmaps = new Map();
    const decoding = new Set();
    const milestones = new Set();
    const loadOrder = [0, freezeFrame, ...Array.from({ length: frameCount }, (_, index) => index).filter(index => index !== 0 && index !== freezeFrame)];
    let loadedCount = 0;
    let pumpIndex = 0;
    let inFlight = 0;
    let displayed = -1;
    let renderedFrame = 0;
    let targetFrame = 0;
    let currentFrame = 0;
    let filmInRange = false;
    let animationFrame = 0;
    let frozenBackdrop = null;
    let filmCompleteReported = false;
    let destroyed = false;
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const decodeFrame = blob => {
      if ("createImageBitmap" in window) {
        const width = matchMedia("(max-width: 600px)").matches ? 720 : 1024;
        return createImageBitmap(blob, { resizeWidth: width, resizeHeight: Math.round(width * frameHeight / frameWidth), resizeQuality: "high" }).catch(() => createImageBitmap(blob));
      }
      return new Promise((resolve, reject) => {
        const image = new Image();
        const url = URL.createObjectURL(blob);
        image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
        image.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Frame decode failed")); };
        image.src = url;
      });
    };

    const closeBitmap = bitmap => { if (typeof bitmap?.close === "function") bitmap.close(); };

    const drawEdgeMatchedBackdrop = (image, x, y, width, height) => {
      context.fillStyle = uniformBackdrop || "#eeece6";
      context.fillRect(0, 0, canvas.width, canvas.height);
      if (!image || uniformBackdrop) return;
      const sourceWidth = image.width;
      const sourceHeight = image.height;
      const right = x + width;
      const bottom = y + height;
      const overlap = 2;
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      if (y > 0) context.drawImage(image, 0, 0, sourceWidth, 1, x, 0, width, y + overlap);
      if (bottom < canvas.height) context.drawImage(image, 0, sourceHeight - 1, sourceWidth, 1, x, bottom - overlap, width, canvas.height - bottom + overlap);
      if (x > 0) context.drawImage(image, 0, 0, 1, sourceHeight, 0, y, x + overlap, height);
      if (right < canvas.width) context.drawImage(image, sourceWidth - 1, 0, 1, sourceHeight, right - overlap, y, canvas.width - right + overlap, height);
      if (x > 0 && y > 0) context.drawImage(image, 0, 0, 1, 1, 0, 0, x + overlap, y + overlap);
      if (right < canvas.width && y > 0) context.drawImage(image, sourceWidth - 1, 0, 1, 1, right - overlap, 0, canvas.width - right + overlap, y + overlap);
      if (x > 0 && bottom < canvas.height) context.drawImage(image, 0, sourceHeight - 1, 1, 1, 0, bottom - overlap, x + overlap, canvas.height - bottom + overlap);
      if (right < canvas.width && bottom < canvas.height) context.drawImage(image, sourceWidth - 1, sourceHeight - 1, 1, 1, right - overlap, bottom - overlap, canvas.width - right + overlap, canvas.height - bottom + overlap);
    };

    const featherIntoUniformBackdrop = (x, y, width, height) => {
      if (!uniformBackdrop) return;
      const match = uniformBackdrop.match(/^#([0-9a-f]{6})$/i);
      if (!match) return;
      const value = Number.parseInt(match[1], 16);
      const red = value >> 16;
      const green = value >> 8 & 255;
      const blue = value & 255;
      const transparent = `rgba(${red}, ${green}, ${blue}, 0)`;
      const feather = Math.min(36, Math.max(20, height * .05));
      const right = x + width;
      const bottom = y + height;
      const topFade = context.createLinearGradient(0, y, 0, y + feather);
      topFade.addColorStop(0, uniformBackdrop);
      topFade.addColorStop(1, transparent);
      context.fillStyle = topFade;
      context.fillRect(x, y, width, feather);
      const bottomFade = context.createLinearGradient(0, bottom - feather, 0, bottom);
      bottomFade.addColorStop(0, transparent);
      bottomFade.addColorStop(1, uniformBackdrop);
      context.fillStyle = bottomFade;
      context.fillRect(x, bottom - feather, width, feather);
      if (x > 0) {
        const leftFade = context.createLinearGradient(x, 0, x + feather, 0);
        leftFade.addColorStop(0, uniformBackdrop);
        leftFade.addColorStop(1, transparent);
        context.fillStyle = leftFade;
        context.fillRect(x, y, feather, height);
      }
      if (right < canvas.width) {
        const rightFade = context.createLinearGradient(right - feather, 0, right, 0);
        rightFade.addColorStop(0, transparent);
        rightFade.addColorStop(1, uniformBackdrop);
        context.fillStyle = rightFade;
        context.fillRect(right - feather, y, feather, height);
      }
    };

    const nearestFrame = index => {
      if (bitmaps.has(index)) return { image: bitmaps.get(index), index };
      for (let distance = 1; distance < frameCount; distance += 1) {
        if (index - distance >= 0 && bitmaps.has(index - distance)) return { image: bitmaps.get(index - distance), index: index - distance };
        if (index + distance < frameCount && bitmaps.has(index + distance)) return { image: bitmaps.get(index + distance), index: index + distance };
      }
      return null;
    };

    const drawFrame = (index, force = false) => {
      if (!force && index === displayed) return;
      const frame = nearestFrame(index);
      if (!frame) return;
      const { image, index: actualIndex } = frame;
      const mobile = matchMedia("(max-width: 600px)").matches;
      const portraitTablet = matchMedia("(min-width: 601px) and (max-width: 1024px) and (orientation: portrait)").matches;
      const scale = Math.min(canvas.width / image.width, canvas.height / image.height) * (mobile ? 1.1 : 1);
      const width = image.width * scale;
      const height = image.height * scale;
      const progress = frameCount > 1 ? actualIndex / (frameCount - 1) : 0;
      const x = (canvas.width - width) / 2;
      const mobilePanProgress = clamp((progress - mobileLiftStart) / Math.max(.01, 1 - mobileLiftStart), 0, 1);
      const startY = mobile ? canvas.height * (.57 - mobilePanProgress * .24) - height / 2 : (canvas.height - height) / 2;
      const liftStart = mobile || portraitTablet ? mobileLiftStart : .16;
      const liftProgress = clamp((progress - liftStart) / (freezeFrame / (frameCount - 1) - liftStart), 0, 1);
      const easedLift = liftProgress * liftProgress * (3 - 2 * liftProgress);
      const y = startY * (1 - easedLift);
      drawEdgeMatchedBackdrop(actualIndex > freezeFrame ? frozenBackdrop : image, x, y, width, height);
      context.drawImage(image, x, y, width, height);
      featherIntoUniformBackdrop(x, y, width, height);
      displayed = index;
      renderedFrame = actualIndex;
      canvas.classList.add("is-ready");
    };

    const sizeCanvas = () => {
      const rect = stage.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      if (canvas.width === width && canvas.height === height) return;
      canvas.width = width;
      canvas.height = height;
      drawFrame(Math.round(currentFrame), true);
    };

    const ensureBitmaps = center => {
      const keep = 22;
      const candidates = [center];
      for (let offset = 1; offset <= 18; offset += 1) {
        if (center + offset < frameCount) candidates.push(center + offset);
        if (center - offset >= 0) candidates.push(center - offset);
      }
      for (const index of candidates) {
        if (decoding.size >= 6) break;
        if (!frameBlobs[index] || bitmaps.has(index) || decoding.has(index)) continue;
        decoding.add(index);
        decodeFrame(frameBlobs[index]).then(bitmap => {
          decoding.delete(index);
          if (destroyed || Math.abs(index - Math.round(currentFrame)) > keep) return closeBitmap(bitmap);
          bitmaps.set(index, bitmap);
          if (index === displayed) drawFrame(index, true);
        }).catch(() => decoding.delete(index)).finally(() => { if (!destroyed) ensureBitmaps(Math.round(currentFrame)); });
      }
      for (const [index, bitmap] of bitmaps) {
        if (Math.abs(index - center) > keep) {
          closeBitmap(bitmap);
          bitmaps.delete(index);
        }
      }
    };

    const updateLoader = () => {
      if (loaderProgress) loaderProgress.style.width = `${loadedCount / frameCount * 100}%`;
      if (loadedCount === frameCount) {
        const label = loader?.firstElementChild;
        if (label) label.textContent = completedLoaderLabel;
        loader?.classList.add("is-complete");
      }
    };

    const pumpFrames = () => {
      while (!destroyed && inFlight < 10 && pumpIndex < loadOrder.length) {
        const index = loadOrder[pumpIndex++];
        inFlight += 1;
        fetch(frames[index], { signal: abortController.signal }).then(response => {
          if (!response.ok) throw new Error(`Frame ${index + 1} failed with ${response.status}`);
          return response.blob();
        }).then(blob => {
          frameBlobs[index] = blob;
          loadedCount += 1;
          updateLoader();
          if (index === freezeFrame) decodeFrame(blob).then(bitmap => {
            closeBitmap(frozenBackdrop);
            frozenBackdrop = bitmap;
            if (currentFrame > freezeFrame) drawFrame(Math.round(currentFrame), true);
          }).catch(() => {});
          if (index === 0) return decodeFrame(blob).then(bitmap => {
            bitmaps.set(0, bitmap);
            sizeCanvas();
            drawFrame(0, true);
            poster?.setAttribute("aria-hidden", "true");
          });
        }).then(() => ensureBitmaps(Math.round(currentFrame))).catch(() => {}).finally(() => {
          inFlight -= 1;
          pumpFrames();
        });
      }
    };

    const filmProgress = () => {
      const rect = section.getBoundingClientRect();
      const stickyTop = parseFloat(getComputedStyle(stage).top) || 0;
      return clamp((stickyTop - rect.top) / Math.max(1, rect.height - stage.offsetHeight), 0, 1);
    };

    const updateComposition = progress => {
      const mobile = matchMedia("(max-width: 600px)").matches;
      const wideDesktop = matchMedia("(min-width: 1025px)").matches;
      const activeOpeningStart = wideDesktop ? openingStartDesktop : openingStart;
      const activeOpeningInDuration = wideDesktop ? openingInDurationDesktop : openingInDuration;
      const activeOpeningHoldEnd = wideDesktop ? openingHoldEndDesktop : openingHoldEnd;
      const activeOpeningOutDuration = wideDesktop ? openingOutDurationDesktop : openingOutDuration;
      const opening = openingStartsHidden
        ? Math.min(
            clamp((progress - activeOpeningStart) / activeOpeningInDuration, 0, 1),
            clamp(1 - (progress - activeOpeningHoldEnd) / activeOpeningOutDuration, 0, 1)
          )
        : clamp(1 - progress / (mobile ? .18 : .22), 0, 1);
      const finalRaw = clamp((progress - (mobile ? finalStartMobile : finalStartDesktop)) / finalDuration, 0, 1);
      const final = finalRaw * finalRaw * (3 - 2 * finalRaw);
      const finalTop = mobile ? 68 - final * 15 : 76 - final * 22;
      const finalActive = final > .9;
      stage.style.setProperty("--film-progress", progress.toFixed(4));
      stage.style.setProperty("--film-opening-alpha", opening.toFixed(4));
      stage.style.setProperty("--film-status-alpha", clamp(1 - (progress - .62) / .1, 0, 1).toFixed(4));
      stage.style.setProperty("--film-final-alpha", final.toFixed(4));
      stage.style.setProperty("--film-final-top", `${finalTop.toFixed(2)}%`);
      finalBeat?.classList.toggle("is-active", finalActive);
      finalBeat?.setAttribute("aria-hidden", String(!finalActive));
      if (finalLink) finalLink.tabIndex = finalActive ? 0 : -1;
      [25, 50, 75].forEach(mark => {
        if (progress >= mark / 100 && !milestones.has(mark)) {
          milestones.add(mark);
          emit("scroll_film_progress", { percent: mark });
        }
      });
      if (progress >= .98 && !filmCompleteReported) {
        filmCompleteReported = true;
        emit("scroll_film_completed");
      }
    };

    const updateTarget = settle => {
      const progress = filmProgress();
      targetFrame = progress * (frameCount - 1);
      if (!settle) return;
      currentFrame = targetFrame;
      ensureBitmaps(Math.round(currentFrame));
      drawFrame(Math.round(currentFrame), true);
      updateComposition(renderedFrame / (frameCount - 1));
    };

    const tick = () => {
      if (!filmInRange || destroyed) return void (animationFrame = 0);
      if (Math.abs(targetFrame - currentFrame) < .04) {
        currentFrame = targetFrame;
        drawFrame(Math.round(currentFrame));
        updateComposition(renderedFrame / (frameCount - 1));
        return void (animationFrame = 0);
      }
      currentFrame += (targetFrame - currentFrame) * .14;
      ensureBitmaps(Math.round(currentFrame));
      drawFrame(Math.round(currentFrame));
      updateComposition(renderedFrame / (frameCount - 1));
      animationFrame = requestAnimationFrame(tick);
    };

    const requestTick = () => {
      if (!filmInRange || destroyed) return;
      updateTarget(false);
      if (!animationFrame) animationFrame = requestAnimationFrame(tick);
    };

    const resize = () => { sizeCanvas(); requestTick(); };
    addEventListener("scroll", requestTick, { passive: true });
    addEventListener("resize", resize);
    const observer = new IntersectionObserver(([entry]) => {
      filmInRange = entry.isIntersecting;
      if (filmInRange) {
        pumpFrames();
        requestTick();
      } else if (animationFrame) {
        cancelAnimationFrame(animationFrame);
        animationFrame = 0;
      }
    }, { rootMargin: "80% 0px", threshold: 0 });
    observer.observe(section);
    pumpFrames();
    sizeCanvas();
    updateTarget(true);

    section._evtolCleanup = () => {
      destroyed = true;
      observer.disconnect();
      abortController.abort();
      removeEventListener("scroll", requestTick);
      removeEventListener("resize", resize);
      if (animationFrame) cancelAnimationFrame(animationFrame);
      bitmaps.forEach(closeBitmap);
      closeBitmap(frozenBackdrop);
    };
  };

  const initMobileAction = hero => {
    if (hero.dataset.evtolActionReady === "true") return;
    hero.dataset.evtolActionReady = "true";
    const cta = hero.querySelector("[data-evtol-inline-cta]");
    const action = page.querySelector("[data-evtol-mobile-action]");
    const film = page.querySelector("[data-evtol-scroll-film]");
    const boundaries = [...page.querySelectorAll("[data-evtol-conversion-boundary]")];
    if (!cta || !action || !("IntersectionObserver" in window)) return;
    let inlinePassed = false;
    let filmVisible = false;
    let boundaryVisible = false;
    let exposed = false;
    const update = () => {
      const dialogOpen = Boolean(page.querySelector("[data-gallery-dialog][open]"));
      const show = inlinePassed && !filmVisible && !boundaryVisible && !dialogOpen;
      action.classList.toggle("is-visible", show);
      action.classList.toggle("is-suppressed", !show);
      action.setAttribute("aria-hidden", String(!show));
      const link = action.querySelector("a");
      if (link) link.tabIndex = show ? 0 : -1;
      if (show && !exposed) { exposed = true; emit("mobile_sticky_exposed"); }
    };
    const ctaObserver = new IntersectionObserver(([entry]) => {
      inlinePassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      update();
    }, { threshold: .1 });
    ctaObserver.observe(cta);
    const filmObserver = film ? new IntersectionObserver(([entry]) => { filmVisible = entry.isIntersecting; update(); }, { threshold: .01 }) : null;
    if (film) filmObserver.observe(film);
    const boundaryObserver = new IntersectionObserver(entries => { boundaryVisible = entries.some(entry => entry.isIntersecting); update(); }, { threshold: .01 });
    boundaries.forEach(boundary => boundaryObserver.observe(boundary));
    hero._evtolActionCleanup = () => {
      ctaObserver.disconnect();
      filmObserver?.disconnect();
      boundaryObserver.disconnect();
    };
  };

  const initNav = nav => {
    if (nav.dataset.evtolReady === "true" || !("IntersectionObserver" in window)) return;
    nav.dataset.evtolReady = "true";
    const links = [...nav.querySelectorAll('a[href^="#"]')];
    const observers = [];
    links.forEach(link => {
      const target = page.querySelector(link.getAttribute("href"));
      if (!target) return;
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        links.forEach(item => item.removeAttribute("aria-current"));
        link.setAttribute("aria-current", "location");
      }, { rootMargin: "-25% 0px -65%", threshold: 0 });
      observer.observe(target);
      observers.push(observer);
    });
    nav._evtolCleanup = () => observers.forEach(observer => observer.disconnect());
  };

  const initScope = scope => {
    scope.querySelectorAll?.("[data-evtol-gallery]").forEach(initGallery);
    scope.querySelectorAll?.("[data-evtol-accordion]").forEach(initAccordion);
    scope.querySelectorAll?.("[data-evtol-estimator]").forEach(initEstimator);
    scope.querySelectorAll?.("[data-evtol-scroll-film]").forEach(initScrollFilm);
    scope.querySelectorAll?.("[data-evtol-hero]").forEach(initMobileAction);
    scope.querySelectorAll?.(".evtol-product-nav").forEach(initNav);
  };

  page.addEventListener("click", event => {
    const tracked = event.target.closest("[data-evtol-event]");
    if (!tracked) return;
    emit(tracked.dataset.evtolEvent, {
      location: tracked.dataset.evtolLocation || "unspecified",
      device: matchMedia("(max-width: 600px)").matches ? "mobile" : "desktop"
    });
  });

  initStickyOffset();
  initScope(page);
  document.addEventListener("shopify:section:load", event => initScope(event.target));
  document.addEventListener("shopify:section:unload", event => {
    event.target.querySelectorAll?.("[data-evtol-gallery], [data-evtol-accordion], [data-evtol-estimator], [data-evtol-scroll-film], [data-evtol-hero], .evtol-product-nav").forEach(element => {
      element._evtolCleanup?.();
      element._evtolActionCleanup?.();
    });
  });
})();
